import React, { useEffect, useState } from 'react';
import { AlertTriangle, Search, Plus, Trash2, Loader2, X } from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input, Select, Button, Modal } from '../../Components/ui';
import { useToast, useConfirm } from '../../Components/Feedback';
import { useApi } from '../../api/useApi';

const GRAVIDADES = [
  { value: 'boa', label: 'Boa (+0,2)' },
  { value: 'muito_boa', label: 'Muito Boa (+0,4)' },
  { value: 'leve', label: 'Mal - Leve (-0,2)' },
  { value: 'media', label: 'Mal - Média (-0,4)' },
  { value: 'grave', label: 'Mal - Grave (-10 / Perde Meritocracia)' },
];

const hojeISO = () => new Date().toISOString().split('T')[0];
const primeiroDiaMesISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Registro de ocorrências de comportamento — parte da reforma da Meritocracia
// (ver frontend/src/Meritocracia.js): educador/coordenador/monitor registram
// aqui, e o desconto já reflete na hora no ranking de pontos. Apagar (soft
// delete no backend) é restrito a coordenador/master, mesmo que o master
// libere o recurso "excluir" pra outro perfil — ver backend/ocorrencias.js.
function Ocorrencias() {
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const { podeCriar, podeExcluir } = usePermissao('/ocorrencias');
  const toast = useToast();
  const confirm = useConfirm();

  const api = useApi();

  const [dataInicio, setDataInicio] = useState(primeiroDiaMesISO());
  const [dataFim, setDataFim] = useState(hojeISO());
  const [ocorrencias, setOcorrencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const res = await api.get(`/api/ocorrencias?inicio=${dataInicio}&fim=${dataFim}`);
      const lista = res?.data ?? (Array.isArray(res) ? res : []);
      setOcorrencias(lista);
    } catch (error) {
      setErro(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (institutionId && token) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, token]);

  // --- Modal "Nova ocorrência" ---
  const [modalAberto, setModalAberto] = useState(false);
  const [buscaAluno, setBuscaAluno] = useState('');
  const [buscaAlunoDebounced, setBuscaAlunoDebounced] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [alunoSelecionado, setAlunoSelecionado] = useState(null);
  const [gravidade, setGravidade] = useState('leve');
  const [descricao, setDescricao] = useState('');
  const [dataOcorrencia, setDataOcorrencia] = useState(hojeISO());
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setBuscaAlunoDebounced(buscaAluno), 300);
    return () => clearTimeout(timer);
  }, [buscaAluno]);

  useEffect(() => {
    if (!buscaAlunoDebounced) {
      setResultadosBusca([]);
      return;
    }
    let cancelado = false;
    api
      .get(`/api/alunos?nome=${encodeURIComponent(buscaAlunoDebounced)}`)
      .then((res) => {
        if (!cancelado) {
          const lista = res?.data ?? (Array.isArray(res) ? res : []);
          setResultadosBusca(lista);
        }
      })
      .catch(() => {
        if (!cancelado) setResultadosBusca([]);
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaAlunoDebounced]);

  const fecharModal = () => {
    setModalAberto(false);
    setBuscaAluno('');
    setResultadosBusca([]);
    setAlunoSelecionado(null);
    setGravidade('leve');
    setDescricao('');
    setDataOcorrencia(hojeISO());
  };

  const registrar = async (e) => {
    e.preventDefault();
    if (!alunoSelecionado) {
      toast.error('Selecione um inscrito.');
      return;
    }
    if (descricao.trim().length < 10) {
      toast.error('Descreva o que aconteceu (pelo menos 10 caracteres).');
      return;
    }
    setSalvando(true);
    try {
      const res = await api.post(`/api/ocorrencias`, {
        id_aluno: alunoSelecionado.id,
        gravidade,
        descricao: descricao.trim(),
        data_ocorrencia: dataOcorrencia,
      });
      toast.success(res?.message || 'Ocorrência registrada.');
      fecharModal();
      carregar();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  const apagar = async (ocorrencia) => {
    if (
      !(await confirm(
        `Apagar a ocorrência de "${ocorrencia.nome_aluno}"? Os pontos dela deixam de ser descontados.`,
        { title: 'Apagar ocorrência', confirmText: 'Apagar' },
      ))
    )
      return;
    try {
      const res = await api.del(`/api/ocorrencias/${ocorrencia.id}`);
      toast.success(res?.message || 'Ocorrência apagada.');
      carregar();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const corGravidade = {
    boa: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    muito_boa: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    leve: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    media: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
    grave: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    gravissima: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  };
  const labelGravidade = {
    boa: 'Boa',
    muito_boa: 'Muito Boa',
    leve: 'Mal - Leve',
    media: 'Mal - Média',
    grave: 'Mal - Grave',
    gravissima: 'Mal - Grave',
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center shadow-sm shrink-0">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Ocorrências
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Registros de comportamento que descontam pontos na Meritocracia.
              </p>
            </div>
          </div>
          {podeCriar && (
            <Button variant="primary" icon={Plus} onClick={() => setModalAberto(true)}>
              Nova Ocorrência
            </Button>
          )}
        </div>

        {erro && (
          <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-700 dark:text-red-400 text-sm">
            {erro}
          </div>
        )}

        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <Input
              type="date"
              label="Início"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
            <Input
              type="date"
              label="Fim"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
            <Button variant="secondary" icon={Search} onClick={carregar}>
              Filtrar
            </Button>
          </div>
        </Card>

        <Card padding="none">
          <div className="p-3">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-3" />
                <span className="text-sm">Carregando...</span>
              </div>
            ) : ocorrencias.length === 0 ? (
              <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">
                Nenhuma ocorrência nesse período.
              </div>
            ) : (
              <div className="space-y-2">
                {ocorrencias.map((o) => (
                  <div
                    key={o.id}
                    className="flex items-start justify-between gap-3 p-2.5 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/40"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p
                          className="text-sm font-medium text-gray-900 dark:text-gray-100"
                          translate="no"
                        >
                          {o.nome_aluno}
                        </p>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${corGravidade[o.gravidade] || 'bg-gray-100 text-gray-700'}`}
                        >
                          {labelGravidade[o.gravidade] || o.gravidade} ({o.pontos !== undefined ? (Number(o.pontos) > 0 ? `+${Number(o.pontos).toFixed(1)}` : `${Number(o.pontos).toFixed(1)}`) : `-${o.percentual_aplicado}%`})
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{o.descricao}</p>
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                        {new Date(`${o.data_ocorrencia}T00:00:00`).toLocaleDateString('pt-BR')} ·
                        registrado por {o.nome_registrado_por || '—'}
                      </p>
                    </div>
                    {podeExcluir && (
                      <button
                        onClick={() => apagar(o)}
                        className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </main>

      <Modal isOpen={modalAberto} onClose={fecharModal} title="Nova Ocorrência" maxWidth="md">
        <form className="space-y-4" onSubmit={registrar}>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
              Inscrito
            </label>
            {alunoSelecionado ? (
              <div className="flex items-center justify-between px-3 h-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <span className="text-sm text-gray-900 dark:text-gray-100" translate="no">
                  {alunoSelecionado.nome}
                </span>
                <button
                  type="button"
                  onClick={() => setAlunoSelecionado(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  placeholder="Buscar inscrito pelo nome..."
                  icon={Search}
                  value={buscaAluno}
                  onChange={(e) => setBuscaAluno(e.target.value)}
                />
                {buscaAlunoDebounced && resultadosBusca.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-auto">
                    {resultadosBusca.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => {
                          setAlunoSelecionado(a);
                          setBuscaAluno('');
                          setResultadosBusca([]);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-100 dark:border-gray-800 last:border-0"
                      >
                        <span translate="no">{a.nome}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <Select
            label="Gravidade"
            value={gravidade}
            onChange={(e) => setGravidade(e.target.value)}
            options={GRAVIDADES}
          />
          <Input
            type="date"
            label="Data da ocorrência"
            value={dataOcorrencia}
            onChange={(e) => setDataOcorrencia(e.target.value)}
            required
          />
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Descrição
            </label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={3}
              placeholder="O que aconteceu..."
              className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={fecharModal}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={salvando}>
              {salvando ? 'Registrando...' : 'Registrar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Ocorrencias;
